/**
 * PRIVA Governance — SAP System Master.
 *
 * Middle of the Multi-Client hierarchy: each SAP System belongs to one Business
 * Client and links to a reusable (encrypted) SAP Connection.
 *
 * The System Identifier (<ClientCode>-<SystemTypeCode>-<Env>, e.g. PS4-EWM-DEV)
 * and the Connection Display Name (<Client Name> <System Type> <Env>, e.g.
 * "PrimeS4 SAP EWM DEV") are generated automatically. The display name may be
 * overridden by platform admins.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Loader2, RefreshCw, ShieldCheck, Plus, Pencil, Trash2, Star, Server,
} from "lucide-react";
import {
  listSmartewmClients, listSmartewmSapSystems, createSmartewmSapSystem,
  updateSmartewmSapSystem, deleteSmartewmSapSystem, setDefaultSmartewmSapSystem,
} from "../lib/adminApi";
import { listConnections } from "../lib/privaAdminApi";
import EnvBadge from "../components/EnvBadge";
import { systemLabel } from "../lib/smartewmLabels";

function extractError(e, fallback = "Request failed.") {
  const msg = e?.response?.data?.detail || e?.message || fallback;
  return typeof msg === "string" ? msg : fallback;
}

const ENVIRONMENTS = ["DEV", "QA", "UAT", "PROD"];
const NONE = "__none__";

const emptyForm = {
  clientSapId: null,
  clientId: "",
  systemModule: "EWM",
  environment: "DEV",
  sapConnectionId: "",
  isDefault: false,
  status: "Active",
};

export default function SapSystemMasterPage() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [connections, setConnections] = useState([]);
  const [systemTypes, setSystemTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    Promise.all([listSmartewmSapSystems(), listSmartewmClients()])
      .then(([sys, cl]) => {
        setRows(sys.sapSystems || []);
        setSystemTypes(sys.systemTypes || []);
        setClients(cl.clients || []);
      })
      .catch((e) => setError(extractError(e, "Failed to load SAP systems.")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load the SAP connections that belong to the selected business client so
  // the "Linked SAP Connection" dropdown only offers valid options.
  const loadConnections = useCallback((clientId) => {
    if (!clientId) { setConnections([]); return; }
    listConnections(clientId)
      .then((data) => setConnections(data.connections || []))
      .catch(() => setConnections([]));
  }, []);

  const clientName = (cid) => clients.find((c) => c.clientId === cid)?.clientName || cid;
  const clientCode = (cid) => clients.find((c) => c.clientId === cid)?.clientCode || "";
  const typeLabel = (code) => systemTypes.find((t) => t.code === code)?.label || code;

  const openCreate = () => {
    const firstClient = clients[0]?.clientId || "";
    setForm({ ...emptyForm, clientId: firstClient });
    loadConnections(firstClient);
    setEditing(false);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      clientSapId: row.clientSapId,
      clientId: row.clientId,
      systemModule: row.systemModule,
      environment: row.environment,
      sapConnectionId: row.sapConnectionId || "",
      isDefault: !!row.isDefault,
      status: row.status || "Active",
    });
    loadConnections(row.clientId);
    setEditing(true);
    setDialogOpen(true);
  };

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onClientChange = (cid) => {
    setForm((f) => ({ ...f, clientId: cid, sapConnectionId: "" }));
    loadConnections(cid);
  };

  // Live previews (mirror the backend generation rules).
  const previewIdentifier = `${clientCode(form.clientId) || "?"}-${form.systemModule}-${form.environment}`;
  const previewLabel = `${typeLabel(form.systemModule)} ${form.environment}`.trim();

  const save = async () => {
    if (!form.clientId) { toast.error("Select a Business Client."); return; }
    setSaving(true);
    const payload = {
      clientId: form.clientId,
      systemModule: form.systemModule,
      environment: form.environment,
      sapConnectionId: form.sapConnectionId || null,
      isDefault: form.isDefault,
      status: form.status,
    };
    try {
      if (editing) {
        await updateSmartewmSapSystem(form.clientSapId, payload);
        toast.success("SAP system updated.");
      } else {
        await createSmartewmSapSystem(payload);
        toast.success("SAP system created.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(extractError(e, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const doSetDefault = async (row) => {
    try {
      await setDefaultSmartewmSapSystem(row.clientSapId);
      toast.success(`"${row.displayName}" is now the default for ${clientName(row.clientId)}.`);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const doDelete = async (row) => {
    if (!window.confirm(`Delete SAP system "${row.displayName}"? This cannot be undone.`)) return;
    try {
      await deleteSmartewmSapSystem(row.clientSapId);
      toast.success("SAP system deleted.");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <div className="p-6 max-w-[1480px] mx-auto" data-testid="sap-system-master-page">
      <header className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <ShieldCheck className="w-4 h-4 text-[#025979]" />
            <p className="text-[10px] uppercase tracking-widest text-[#025979] font-semibold">PRIVA Governance</p>
          </div>
          <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>SAP System Master</h1>
          <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
            SAP landscapes per Business Client. The System Identifier and display
            name are generated automatically from the client + system type + environment.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <button type="button" onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#025979] border border-[#025979] rounded-sm hover:bg-[#025979]/5" data-testid="sap-system-master-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <Button onClick={openCreate} disabled={clients.length === 0} className="h-8 bg-[#025979] hover:bg-[#03738C] text-xs" data-testid="sap-system-master-create-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New SAP System
          </Button>
        </div>
      </header>

      {clients.length === 0 && !loading && (
        <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800" data-testid="sap-system-master-no-clients">
          Create a Business Client first — SAP Systems must belong to a client.
        </div>
      )}
      {error && <div className="text-red-600 text-xs py-2" data-testid="sap-system-master-error">{error}</div>}

      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-x-auto">
        <table className="min-w-full text-xs" data-testid="sap-system-master-table">
          <thead className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <Th>Display Name</Th><Th>Business Client</Th><Th>System Type</Th><Th>Environment</Th>
              <Th>Connection</Th><Th>Default</Th><Th>Status</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12 text-[#6B7280]"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-[#6B7280]" data-testid="sap-system-master-empty">No SAP systems yet.</td></tr>
            ) : (
              rows.map((r) => {
                const conn = connections.find((c) => c.connectionId === r.sapConnectionId);
                return (
                  <tr key={r.clientSapId} className="hover:bg-[#F8FAFC]/70" data-testid={`sap-system-master-row-${r.clientSapId}`}>
                    <Td className="font-medium text-[#111827]">
                      <span className="inline-flex items-center gap-1.5">
                        <Server className="w-3.5 h-3.5 text-[#025979]" />{systemLabel(r)}
                        <EnvBadge env={r.environment} />
                      </span>
                    </Td>
                    <Td className="text-[#374151]">{clientName(r.clientId)}</Td>
                    <Td>{r.systemTypeLabel}</Td>
                    <Td><EnvBadge env={r.environment} /></Td>
                    <Td className="text-[#374151]">{conn?.connectionName || (r.sapConnectionId ? "Linked" : <span className="text-[#9CA3AF]">—</span>)}</Td>
                    <Td>{r.isDefault ? <Badge className="bg-[#E0F2FE] text-[#025979] border border-[#025979]/20">Default</Badge> : <span className="text-[#9CA3AF]">—</span>}</Td>
                    <Td>
                      <Badge className={r.status === "Active"
                        ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                        : "bg-gray-100 text-gray-600 border border-gray-200"}>
                        {r.status === "Active" ? "Active" : "Inactive"}
                      </Badge>
                    </Td>
                    <Td>
                      <div className="flex items-center gap-1.5">
                        <IconBtn title="Edit" onClick={() => openEdit(r)} testId={`sap-system-master-edit-${r.clientSapId}`}><Pencil className="w-3.5 h-3.5" /></IconBtn>
                        {!r.isDefault && <IconBtn title="Set default" onClick={() => doSetDefault(r)} testId={`sap-system-master-default-${r.clientSapId}`}><Star className="w-3.5 h-3.5" /></IconBtn>}
                        <IconBtn title="Delete" onClick={() => doDelete(r)} testId={`sap-system-master-delete-${r.clientSapId}`} danger><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                      </div>
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="sap-system-master-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit SAP System" : "New SAP System"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Field label="Business Client *" full>
              <Select value={form.clientId} onValueChange={onClientChange} disabled={editing}>
                <SelectTrigger className="h-8 text-xs" data-testid="sap-system-master-form-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.clientId} value={c.clientId}>{c.clientName}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="SAP System Type *">
              <Select value={form.systemModule} onValueChange={setField("systemModule")} disabled={editing}>
                <SelectTrigger className="h-8 text-xs" data-testid="sap-system-master-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {systemTypes.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Environment *">
              <Select value={form.environment} onValueChange={setField("environment")} disabled={editing}>
                <SelectTrigger className="h-8 text-xs" data-testid="sap-system-master-form-env"><SelectValue /></SelectTrigger>
                <SelectContent>{ENVIRONMENTS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Linked SAP Connection" full>
              <Select value={form.sapConnectionId || NONE} onValueChange={(v) => setField("sapConnectionId")(v === NONE ? "" : v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="sap-system-master-form-connection"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None (use tenant default)</SelectItem>
                  {connections.map((c) => <SelectItem key={c.connectionId} value={c.connectionId}>{c.connectionName} ({c.environment})</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <div className="flex items-center justify-between gap-2 pt-2">
              <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">Default System</span>
              <Switch checked={form.isDefault} onCheckedChange={setField("isDefault")} data-testid="sap-system-master-form-default" />
            </div>
            <div className="flex items-center justify-between gap-2 pt-2">
              <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">Status — {form.status === "Active" ? "Active" : "Inactive"}</span>
              <Switch checked={form.status === "Active"} onCheckedChange={(v) => setField("status")(v ? "Active" : "Disabled")} data-testid="sap-system-master-form-status" />
            </div>
          </div>
          {/* Auto-generated identifiers preview */}
          <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-3 py-2 text-[11px] space-y-1" data-testid="sap-system-master-preview">
            <div><span className="text-[#9CA3AF] uppercase tracking-wider text-[10px]">System Identifier: </span><span className="font-mono text-[#025979]">{editing ? form.clientSapId : previewIdentifier}</span></div>
            <div className="flex items-center gap-1.5"><span className="text-[#9CA3AF] uppercase tracking-wider text-[10px]">Display Name: </span><span className="text-[#374151]">{previewLabel}</span><EnvBadge env={form.environment} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="h-8 text-xs" data-testid="sap-system-master-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="h-8 bg-[#025979] hover:bg-[#03738C] text-xs" data-testid="sap-system-master-form-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (editing ? "Save Changes" : "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children }) { return <th className="text-left font-medium px-3 py-2 whitespace-nowrap">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>; }
function Field({ label, hint, children, full }) {
  return (
    <label className={`flex flex-col gap-1 text-[10px] uppercase tracking-wider text-[#6B7280] ${full ? "col-span-2" : ""}`}>
      {label}{children}
      {hint && <span className="text-[10px] normal-case tracking-normal text-[#9CA3AF]">{hint}</span>}
    </label>
  );
}
function IconBtn({ children, onClick, title, testId, danger }) {
  return (
    <button type="button" title={title} onClick={onClick} data-testid={testId}
      className={`p-1.5 rounded-sm border transition-colors ${danger ? "border-red-200 text-red-600 hover:bg-red-50" : "border-[#E5E7EB] text-[#374151] hover:bg-[#F8FAFC]"}`}>
      {children}
    </button>
  );
}
